import React from "react";
import { Helmet } from "react-helmet-async";
import { MapPin, Phone, Mail, Clock, MessageCircle } from "lucide-react";
import EnhancedEnquiryForm from "../components/EnhancedEnquiryForm";
import { usePage } from "../hooks/useApi";
import { pageApi } from "../services/api";

const ContactPage: React.FC = () => {
  const { data: page, isLoading, error } = usePage("contact");

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#FF5A1F] mx-auto mb-4"></div>
          <p className="text-[#334155]">Loading...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">
            Error loading page: {error.message}
          </p>
          <p className="text-[#334155]">Using fallback content...</p>
        </div>
      </div>
    );
  }

  // Helper function to get field value with fallback
  const getField = (key: string, fallback: string = ""): string => {
    if (!page) return fallback;
    const fields = pageApi.getPageFields(page);
    return fields[key] || fallback;
  };
  const contactInfo = [
    {
      icon: MapPin,
      title: getField("office_title", "Visit Our Office"),
      details: [
        getField("office_address", "7 Byron St"),
        getField("office_city", "Rolleston"),
      ],
      action: getField("office_action", "Get Directions"),
      link: "https://www.google.com/maps/dir//7+Byron+Street,+Rolleston+7614,+Nova+Zel%C3%A2ndia/@-43.5924604,172.3791527,17z",
    },
    {
      icon: Phone,
      title: getField("phone_title", "Call Us"),
      details: [
        getField("phone_number", "021 550 257"),
        getField("phone_hours_weekdays", "Mon-Fri: 8:00 AM - 5:00 PM"),
        getField("phone_hours_weekend", "Sat: 9:00 AM - 2:00 PM"),
      ],
      action: getField("phone_action", "Call Now"),
      link: "tel:021550257",
    },
    {
      icon: Mail,
      title: getField("email_title", "Email Us"),
      details: [
        getField("email_primary", "Phil@rollestonhomes.co.nz"),
      ],
      action: getField("email_action", "Send Email"),
      link: "mailto:Phil@rollestonhomes.co.nz",
    },
  ];

  const faqs = [
    {
      question: getField(
        "faq1_question",
        "How long does it take to build a home?"
      ),
      answer: getField(
        "faq1_answer",
        "Typically, our homes take 16-20 weeks to complete from the start of construction. This timeline can vary depending on the size and complexity of your home, weather conditions, and any custom features you choose."
      ),
    },
    {
      question: getField(
        "faq2_question",
        "Do you provide warranties on your homes?"
      ),
      answer: getField(
        "faq2_answer",
        "Yes, all our homes come with comprehensive warranties. We provide a 10-year structural warranty and various warranties on fixtures, fittings, and appliances. We also offer ongoing support and maintenance services."
      ),
    },
    {
      question: getField(
        "faq3_question",
        "Can I make changes to your existing home designs?"
      ),
      answer: getField(
        "faq3_answer",
        "Absolutely! Our home designs can be customized to suit your specific needs and preferences. Our design team will work with you to modify layouts, add features, or create a completely custom design."
      ),
    },
    {
      question: getField("faq4_question", "What areas do you service?"),
      answer: getField(
        "faq4_answer",
        "We primarily service the Canterbury region, including Rolleston, Christchurch, Lincoln, Prebbleton, and surrounding areas. Contact us to confirm if we build in your specific location."
      ),
    },
    {
      question: getField("faq5_question", "How do I get started?"),
      answer: getField(
        "faq5_answer",
        "Simply contact us for a free initial consultation. We'll discuss your needs, budget, and timeline, then guide you through our design and building process step by step."
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Helmet>
        <title>Contact Us - Rolleston Homes | Get In Touch Today</title>
        <meta
          name="description"
          content="Contact Rolleston Homes for your home building needs. Call +64 3 345 6789 or visit our Rolleston office. Free consultations available."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-white py-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold text-[#1F2937] mb-6">
            {getField("hero_title", "Get In Touch")}
          </h1>
          <p className="text-lg text-[#334155] max-w-2xl mx-auto">
            {getField(
              "hero_subtitle",
              "Ready to start your home building journey? We'd love to hear from you. Contact us today for a free consultation and let's discuss how we can bring your vision to life."
            )}
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg p-8 text-center hover:shadow-xl transition-shadow duration-200"
              >
                <div className="w-16 h-16 bg-[#FF5A1F]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <info.icon className="w-8 h-8 text-[#FF5A1F]" />
                </div>
                <h3 className="text-xl font-bold text-[#1F2937] mb-4">
                  {info.title}
                </h3>
                <div className="space-y-2 mb-6">
                  {info.details.map((detail, detailIndex) => (
                    <p key={detailIndex} className="text-[#334155]">
                      {detail}
                    </p>
                  ))}
                </div>
                <a 
                  href={info.link}
                  target={info.link?.startsWith('http') ? '_blank' : undefined}
                  rel={info.link?.startsWith('http') ? 'noopener noreferrer' : undefined}
                  className="text-[#FF5A1F] font-semibold hover:text-[#e4501c] transition-colors duration-200"
                >
                  {info.action}
                </a>
              </div>
            ))}
          </div>

          {/* Contact Form and Map */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <EnhancedEnquiryForm showProgress={true} variant="detailed" />
            </div>

            {/* Map and Additional Info */}
            <div className="space-y-8">
              {/* Map */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="h-64 md:h-96">
                  <iframe 
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2889.676201332066!2d172.3791527!3d-43.5924604!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6d320337a2bc3d2f%3A0xa41f9e57d466331!2s7%20Byron%20Street%2C%20Rolleston%207614%2C%20Nova%20Zel%C3%A2ndia!5e0!3m2!1spt-BR!2sau!4v1759640667166!5m2!1spt-BR!2sau" 
                    width="100%" 
                    height="100%" 
                    style={{ border: 0 }} 
                    allowFullScreen 
                    loading="lazy" 
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Rolleston Homes Location"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-[#1F2937] mb-2">
                    {getField(
                      "display_centre_title",
                      "Visit Our Office"
                    )}
                  </h3>
                  <p className="text-[#334155] text-sm mb-2">
                    7 Byron St, Rolleston
                  </p>
                  <p className="text-[#334155] text-sm mb-4">
                    {getField(
                      "display_centre_description",
                      "Come and see our latest home designs and speak with our experienced team. No appointment necessary during business hours."
                    )}
                  </p>
                  <a 
                    href="https://www.google.com/maps/dir//7+Byron+Street,+Rolleston+7614,+Nova+Zel%C3%A2ndia/@-43.5924604,172.3791527,17z"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-[#FF5A1F] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#e4501c] transition-colors duration-200"
                  >
                    {getField("display_centre_action", "Get Directions")}
                  </a>
                </div>
              </div>

              {/* Business Hours */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Clock className="w-6 h-6 text-[#FF5A1F]" />
                  <h3 className="text-lg font-bold text-[#1F2937]">
                    {getField("business_hours_title", "Business Hours")}
                  </h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#334155]">Monday - Friday</span>
                    <span className="font-medium text-[#1F2937]">
                      {getField("business_hours_weekdays", "8:00 AM - 5:00 PM")}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#334155]">Saturday</span>
                    <span className="font-medium text-[#1F2937]">
                      {getField("business_hours_saturday", "9:00 AM - 2:00 PM")}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#334155]">Sunday</span>
                    <span className="font-medium text-[#1F2937]">
                      {getField("business_hours_sunday", "Closed")}
                    </span>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-[#FF5A1F]/10 rounded-lg">
                  <p className="text-sm text-[#FF5A1F] font-medium">
                    {getField(
                      "business_hours_emergency",
                      "Emergency contact available 24/7 for existing clients"
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white py-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1F2937] mb-6">
              {getField("faq_title", "Frequently Asked Questions")}
            </h2>
            <p className="text-lg text-[#334155] max-w-2xl mx-auto">
              {getField(
                "faq_subtitle",
                "Find answers to common questions about our home building process and services."
              )}
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-[#FAFAFA] rounded-2xl p-6">
                <h3 className="text-lg font-bold text-[#1F2937] mb-3 flex items-start space-x-3">
                  <MessageCircle className="w-5 h-5 text-[#FF5A1F] mt-0.5 flex-shrink-0" />
                  <span>{faq.question}</span>
                </h3>
                <p className="text-[#334155] leading-relaxed pl-8">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-[#334155] mb-4">
              Have a question that's not answered here?
            </p>
            <button className="bg-[#FF5A1F] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#e4501c] transition-colors duration-200">
              Contact Us Directly
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1F2937] py-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6">
            {getField("cta_title", "Ready to Build Your Dream Home?")}
          </h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-8">
            {getField(
              "cta_subtitle",
              "Don't wait any longer. Contact us today and take the first step towards your new home. Our team is ready to help you every step of the way."
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={getField("cta_button1_link", "tel:+6433456789")}
              className="bg-[#FF5A1F] text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-[#e4501c] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              {getField("cta_button1_text", "Call Now: +64 3 345 6789")}
            </a>
            <a
              href={getField("cta_button2_link", "/homes")}
              className="border-2 border-white text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-white hover:text-[#1F2937] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              {getField("cta_button2_text", "Browse Our Homes")}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
